/**
 * Chip - Toggleable Pill Button Component
 *
 * Renders as Box (button). Used for filters, tags, and multi-select options.
 * Pattern derived from FilterPanel multiselect/radio buttons.
 *
 * @example
 * <Chip selected={true} onClick={() => {}}>1K</Chip>
 * <Chip selected={false} onClick={() => {}}>2LDK</Chip>
 *
 * @example
 * <Chip disabled>Unavailable</Chip>
 */
import { forwardRef } from "react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface ChipProps extends BaseComponentProps {
  /** Selected/active state */
  selected?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Chip component
 * Renders as a Box button with rounded-full styling
 */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  ({ selected, onClick, disabled, className, children, ...props }, ref) => {
    return (
      <Box
        as="button"
        type="button"
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "px-3 py-1.5 text-sm rounded-full border transition-colors active:scale-[0.98]",
          selected
            ? "bg-red-600 border-red-600 text-white"
            : "bg-white border-gray-300 text-gray-700 hover:border-red-300",
          disabled && "opacity-50 cursor-not-allowed hover:border-gray-300",
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

Chip.displayName = "Chip";
